import React, { useState, useRef, useEffect } from 'react';
import { Container, Nav, Navbar, Form, Modal, CloseButton } from 'react-bootstrap';
import { ClockHistory, Search, Person, QuestionCircle, CalendarHeart, List, Wallet2, PlayCircle } from 'react-bootstrap-icons';
import { useTheme } from '../ThemeContext';
import { useAuth } from './AuthContext';
import { useLocation, NavLink, Link } from 'react-router-dom';
import SalesFeed from './SalesFeed';
import { debounce } from 'lodash';
import { useFilter } from './FilterContext';

const customStyles = (theme) => ({
  navbar: {
    height: '60px',
    boxShadow: '0 -2px 20px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
  },
  icon: {
    transition: 'all 0.2s ease',
    padding: '8px',
    borderRadius: '50%',
    position: 'relative',
    color: theme === 'dark' ? '#fff' : '#000',
  },
  activeIcon: {
    color: theme === 'dark' ? '#4CAF50' : '#2E7D32',
    transform: 'scale(1.15)'
  },
  activeIconWallet: {
    color: theme === 'dark' ? '#0088c2' : '#006699',
    transform: 'scale(1.15)',
    background: theme === 'dark'
      ? 'rgba(0, 136, 194, 0.2)'
      : 'rgba(0, 102, 153, 0.1)',
  },
  searchModal: {
    modalContent: {
      border: 'none',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
      overflow: 'hidden',
      backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
      maxWidth: '90vw', // Ограничиваем максимальную ширину
      margin: '1rem', // Добавляем отступы от краев экрана
      width: '600px', // Фиксированная ширина для десктопов
      height: 'auto'
    },
    input: {
      fontSize: '1rem', // Уменьшаем размер шрифта
      padding: '10px 14px 10px 2.5rem', // Уменьшаем паддинги
      borderRadius: '25px',
      border: 'none',
    }
  }
});

const BottomNav = () => {
  const { theme } = useTheme();
  const { searchSales } = useAuth();
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isWaitingListPage = location.pathname === '/waiting-list'
  const [searchResults, setSearchResults] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const { showFeedbackOnly, setShowFeedbackOnly } = useFilter();
  const themeColors = {
    green: theme === 'dark' ? '#4CAF50' : '#2E7D32',
    yellow: theme === 'dark' ? '#FFD700' : '#F9A825',
    blue: theme === 'dark' ? '#64B5F6' : '#1976D2',
    red: theme === 'dark' ? '#EF5350' : '#D32F2F',
  };
  const infoModalStyles = {
    image: {
      borderRadius: '7px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      margin: '20px auto',
      width: '40px',
      border: `1px solid ${theme === 'dark' ? '#333' : '#eee'}`,
    },
    section: {
      marginBottom: '32px',
      padding: '16px',
      background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      borderRadius: '12px',
    },
    title: {
      fontSize: '1.3rem',
      fontWeight: '600',
      marginBottom: '20px',
      color: theme === 'dark' ? '#fff' : '#333',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    listItem: {
      alignItems: 'start',
      gap: '12px',
      marginBottom: '16px',
      padding: '12px',
      borderRadius: '8px',
      background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8f9fa',
    },
    dot: {
      width: '14px',
      height: '14px',
      borderRadius: '50%',
      flexShrink: 0,
      marginTop: '4px',
    },
    divider: {
      borderTop: `1px solid ${theme === 'dark' ? '#333' : '#eee'}`,
      margin: '24px 0',
    },
    textHighlight: {
      padding: '4px 8px',
      borderRadius: '6px',
      background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    },
  };
  const themeClass = theme === 'dark'
    ? 'text-white bg-dark'
    : 'text-dark bg-light';

  const iconHoverStyle = {
    transform: 'scale(1.1)',
    background: theme === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'
  };
  const activePaths = {
    '/': <List size={26} />,
    '/waiting-list': <ClockHistory size={26} />,
    '/yesterday-sales': <CalendarHeart size={26} />,
    '/profile': <Person size={26} />
  };

  const debouncedSearch = useRef(
    debounce(async (text) => {
      if (!text) return;
      const type = /^\d+$/.test(text) ? 'art' : 'name';
      try {
        const data = await searchSales(type, text);
        setSearchResults(data);
      } catch (error) {
        console.error('Search error:', error);
      }
    }, 800)
  ).current;

  const handleSearch = (text) => {
    setSearchText(text);
    debouncedSearch(text);
  };


  const openSearchModal = () => {
    setShowSearchModal(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      debouncedSearch.flush();
    }
  };
  const styles = customStyles(theme);
  useEffect(() => {
    // Определяем элементы меню в зависимости от страницы
    const items = [];
    if (!isHomePage) items.push({ path: '/', icon: <List />, label: 'Главная' });
    if (!isWaitingListPage) items.push({ path: '/waiting-list', icon: <ClockHistory />, label: 'Лист ожидания' });
    items.push(
      { path: '/yesterday-sales', icon: <CalendarHeart />, label: 'Вчерашние скидки' },
      { path: '/profile', icon: <Person />, label: 'Профиль' }
    );
    setMenuItems(items);
  }, [location.pathname]);
  const menuModalStyles = {
    modal: {
      position: 'fixed',
      bottom: '60px',
      right: '10px',
      left: '10px',
      margin: 0,
      border: 'none',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
      backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
      maxWidth: 'calc(100% - 20px)',
      transform: showMenuModal ? 'translateY(0)' : 'translateY(120%)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    item: {
      padding: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      color: theme === 'dark' ? '#fff' : '#000',
      transition: 'background 0.2s',
      cursor: 'pointer',
    }
  };

  return (
    <>
      <Navbar
        fixed="bottom"
        bg={theme}
        variant={theme}
        style={styles.navbar}
        className="navbar"
      >
        <Container fluid className="px-3">
          <Nav className="d-flex justify-content-between w-100 align-items-center">


            {/* Кнопка меню */}
            <Nav.Link
              onClick={() => setShowMenuModal(prev => !prev)}
              style={styles.icon}
            >
              <List size={26} />
            </Nav.Link>

            {/* Поиск только на главной */}
            {isHomePage && (
              <Nav.Link
                onClick={openSearchModal}
                style={styles.icon}
              >
                <Search size={26} />
              </Nav.Link>
            )}
            {isHomePage && (
              <Nav.Link
                onClick={() => setShowFeedbackOnly(!showFeedbackOnly)}
                style={{
                  ...styles.icon,
                  ...(showFeedbackOnly ? styles.activeIconWallet : {}),
                }}
              >
                <Wallet2 size={26} />
              </Nav.Link>
            )}
            {/* Иконка информации */}
            {(isHomePage || isWaitingListPage) && (
              <Nav.Link
                onClick={() => setShowInfoModal(true)}
                style={styles.icon}
              >
                <QuestionCircle size={26} />
              </Nav.Link>
            )}
          </Nav>
        </Container>
      </Navbar>

      <Modal
        show={showMenuModal}
        onHide={() => setShowMenuModal(false)}
        animation={true}
        backdropClassName="menu-backdrop"
        dialogClassName="menu-modal"
        centered={false}
        transition={null} // Отключаем стандартную анимацию
        enforceFocus={false}
      >
        <Modal.Body
          style={{
            padding: '0',
            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
            borderRadius: '15px'
          }}
        >
          {menuItems.map((item) => (
            <Nav.Link
              key={item.path}
              as={NavLink}
              to={item.path}
              style={menuModalStyles.item}
              onClick={() => setShowMenuModal(false)}
            >
              {React.cloneElement(item.icon, { size: 20 })}
              {item.label}
            </Nav.Link>
          ))}
        </Modal.Body>
      </Modal>

      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        centered
        backdropClassName="search-backdrop"
        style={styles.searchModal.modalContent}
        contentClassName="overflow-hidden" // Добавляем класс для скрытия переполнения
      >
        <div className="p-2 scrollable">
          <div className="d-flex align-items-center gap-2 px-2">
            <div className="position-relative flex-grow-1">
              <Form.Control
                ref={searchInputRef}
                type="search"
                placeholder="Артикул или название..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className={theme === 'dark' ? 'bg-dark text-white border-secondary' : 'bg-light'}
                style={styles.searchModal.input}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                className="position-absolute"
                style={{
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fill: theme === 'dark' ? '#adb5bd' : '#6c757d',
                  pointerEvents: 'none'
                }}
                viewBox="0 0 24 24"
              >
                <path d="M10 18a7.952 7.952 0 0 0 4.897-1.688l4.396 4.396 1.414-1.414-4.396-4.396A7.952 7.952 0 0 0 18 10c0-4.411-3.589-8-8-8s-8 3.589-8 8 3.589 8 8 8zm0-14c3.309 0 6 2.691 6 6s-2.691 6-6 6-6-2.691-6-6 2.691-6 6-6z" />
              </svg>
            </div>
            <CloseButton
              variant={theme === 'dark' ? 'white' : 'dark'}
              onClick={() => setShowSearchModal(false)}
            />
          </div>

          {searchResults.length > 0 ? (
            <div style={{
              margin: '10px 0 0 -20px'
            }}>
              <SalesFeed
                sales={searchResults}
                type="modal"
              />
            </div>

          ) : searchText && (
            <div className="text-center py-2 px-2">
              <div style={{ color: theme === 'dark' ? '#fff' : '#666' }}>
                Ничего не найдено
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        contentClassName="rounded-lg shadow"
        size="lg"
      >
        <Modal.Header

          className={`${themeClass} border-0`}
          style={{
            ...customStyles.modalHeader,
            padding: '1.5rem 1.5rem 0 1.5rem',
          }}
        >
          <Modal.Title style={infoModalStyles.title}>
            <QuestionCircle size={24} />
            Описание элементов
          </Modal.Title>
          <CloseButton
            onClick={() => setShowInfoModal(false)}
            className={theme === 'dark' ? 'btn-close-white' : 'btn-close-black'}
          />
        </Modal.Header>
        <Modal.Body
          className={`${themeClass} scrollable`}
          style={{
            maxHeight: '70vh',
            padding: '0 1.5rem',
            lineHeight: '1.6',
          }}
        >
          <div className="sb-inline-cta">
            <Link to="/videos" className="sb-mini-link" aria-label="Видеоинструкция">
              <PlayCircle size={16} /> Видеоинструкция
            </Link>
          </div>
          {isHomePage && (
            <>
              {/* Остатки на складе */}
              <div style={infoModalStyles.section}>
                <h5 style={infoModalStyles.title}>📦 Остатки на складе</h5>
                <img
                  src="/ostatki.png"
                  alt="Пример остатков"
                  style={infoModalStyles.image}
                />
                <div style={infoModalStyles.listItem}>
                  <div style={{ ...infoModalStyles.dot, background: themeColors.green }} />
                  <div>Склад Wildberries</div>
                </div>
                <div style={infoModalStyles.listItem}>
                  <div style={{ ...infoModalStyles.dot, background: themeColors.yellow }} />
                  <div>Склад продавца</div>
                </div>
              </div>

              {/* Размеры */}
              <div style={infoModalStyles.section}>
                <h5 style={infoModalStyles.title}>📏 Размеры</h5>
                <p>
                  Размеры отображаются с тем же цветовым кодированием, что и остатки. Если они не помещаются в карточке, их можно прокручивать.
                </p>
              </div>

              {/* AI Прогноз */}
              <div style={infoModalStyles.section}>
                <h5 style={infoModalStyles.title}>🤖 AI Прогноз</h5>
                <div style={infoModalStyles.listItem}>
                  <div
                    style={{
                      ...infoModalStyles.dot,
                      background: 'linear-gradient(45deg, #4CAF50, #64B5F6)',
                    }}
                  />
                  <div>
                    Вероятность выкупа в %, предсказанная ИИ на основе нашей базы данных.
                    <br />
                    <small>*Фильтры доступны в настройках.</small>
                  </div>
                </div>
              </div>

              {/* Цена */}
              <div style={infoModalStyles.section}>
                <h5 style={infoModalStyles.title}>💰 Цена</h5>
                <div style={infoModalStyles.listItem}>
                  <div style={{ ...infoModalStyles.dot, background: themeColors.green }} />
                  <div>Цена не изменилась</div>
                </div>
                <div style={infoModalStyles.listItem}>
                  <div style={{ ...infoModalStyles.dot, background: themeColors.blue }} />
                  <div>Цена снизилась</div>
                </div>
                <div style={infoModalStyles.listItem}>
                  <div style={{ ...infoModalStyles.dot, background: themeColors.red }} />
                  <div>Цена повысилась</div>
                </div>
              </div>

              {/* Диапазон цен */}
              <div style={infoModalStyles.section}>
                <h5 style={infoModalStyles.title}>📊 Диапазон цен</h5>
                <p>Показывает минимальную и максимальную цену за период отслеживания.</p>
              </div>
              {/* Остальные метки */}
              <div style={infoModalStyles.section}>
                <h5 style={infoModalStyles.title}>⋯ Остальные метки</h5>
                <div style={infoModalStyles.listItem}>
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      style={{
                        filter: 'drop-shadow(0 0 8px rgba(255, 68, 68, 0.5))',
                        position: 'relative',
                        fill: 'red'
                      }}
                      viewBox="0 0 19 18"
                    >
                      <path fill="inherit" fillRule="evenodd" d="M10.6 5.1a.9.9 0 0 0-.9.9v3.2a.9.9 0 1 0 1.8 0V6a.9.9 0 0 0-.9-.9Zm0 7.8a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z" clipRule="evenodd" />
                      <path fill="inherit" fillRule="evenodd" d="M10.072 2.18a6.703 6.703 0 0 0-3.653 1.455.974.974 0 0 1-.134.09c-.341.191-.774.163-1.057-.12-.322-.323-.327-.851.021-1.144a8.378 8.378 0 0 1 .342-.273A8.326 8.326 0 0 1 9.941.527a8.312 8.312 0 0 1 5.62 1.61 8.508 8.508 0 0 1 3.22 4.935 8.6 8.6 0 0 1-.737 5.869 8.43 8.43 0 0 1-4.339 3.96 8.289 8.289 0 0 1-5.838.132 8.415 8.415 0 0 1-4.51-3.758A8.584 8.584 0 0 1 2.27 8.067l-.856.744a.831.831 0 0 1-1.186-.037.858.858 0 0 1 .036-1.201l2.453-2.338a.833.833 0 0 1 1.223.079c.05.063.092.116.132.17.081.104.162.209.322.389l1.718 2.114a.858.858 0 0 1-.115 1.197.832.832 0 0 1-1.18-.116L3.931 7.98a6.917 6.917 0 0 0 .84 4.46 6.774 6.774 0 0 0 3.63 3.026 6.672 6.672 0 0 0 4.7-.107 6.785 6.785 0 0 0 3.492-3.187c.75-1.452.96-3.128.593-4.724a6.848 6.848 0 0 0-2.593-3.972 6.69 6.69 0 0 0-4.522-1.296Z" clipRule="evenodd" />
                    </svg>
                    <p>Если горит такая иконка, значит товар невозвратный</p>
                  </div>
                </div>
                <div style={infoModalStyles.listItem}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: '#0088c2',
                    borderRadius: 10,
                    height: 23,
                    width: '78px',
                    padding: '0 5px'
                  }}>
                    <Wallet2 size={14} color="white" style={{ marginRight: 4 }} />


                    <span style={{ color: 'white', fontSize: 15, fontWeight: '600' }}>
                      +200₽
                    </span>
                  </div>
                  <p>
                    Показывает рубли за отзыв, если сумма изменилась после того как попала в бот, будет отображаться старая сумма и новая
                  </p>
                </div>

                <div style={infoModalStyles.listItem}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: '#dc3545',
                    borderRadius: 10,
                    height: 23,
                    width: '78px',
                    padding: '0 5px'
                  }}>
                    <Wallet2 size={14} color="white" style={{ marginRight: 4 }} />


                    <span style={{
                      color: 'white',
                      textDecoration: 'line-through',
                      fontSize: 15,
                      fontWeight: '600'
                    }}>
                      200₽
                    </span>

                  </div>
                  <p>
                    Это означает что пропала плашка рубли за отзыв
                  </p>
                </div>

                <div style={infoModalStyles.listItem}>
                  <div
                    style={{

                      color: '#fff',
                      borderRadius: '10px',
                      padding: '2px 6px',
                      overflow: 'hidden',
                      alignSelf: 'center',
                      fontSize: '12px',
                      fontWeight: '600',
                      width: '78px',
                      backgroundColor: '#0088c2',
                    }}
                  >
                    -100 (50%)

                  </div>
                  <p>
                    Здесь считается ваша выгода исходя из рублей за бонусы:
                  </p>
                  <p>
                    — Если число с <b>минусом</b> (например, <b>-50</b>) — это сумма, которую по итогу вы заплатите с учетом начисления рублей за отзыв.
                  </p>
                  <p>
                    — Если число <b>без минуса</b> (например, <b>+100</b>) — это ваш доход.</p>
                  <p>
                    Процент (%) показывает долю выгоды или переплаты.
                  </p>
                  <p>
                    если сумма рублей за отзыв изменилась после того как попала в бот, будет отображаться старый и новый расчет.
                  </p>
                </div>
                <div style={infoModalStyles.listItem}>
                  <div>
                    <Wallet2 size={26} />
                    <p>При нажатии на эту иконку в меню будут отображаться только те товары где есть рубли за отзыв</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {isWaitingListPage && (
            <div style={infoModalStyles.section}>
              <h5 style={infoModalStyles.title}>Лист Отслеживания 🛒</h5>
              <div>
                <div style={{ ...infoModalStyles.listItem, flexDirection: 'column', gap: '16px' }}>
                  <strong>1. Добавление товара</strong>
                  <p>Введите артикул Wildberries для начала отслеживания.</p>
                </div>
                <div style={{ ...infoModalStyles.listItem, flexDirection: 'column', gap: '16px' }}>
                  <strong>2. Выбор размеров</strong>
                  <p>
                    Отметьте нужные размеры и выберите склад:
                    <br />• <span style={{ color: themeColors.green }}>Зеленый</span> — склад Wildberries
                    <br />• <span style={{ color: themeColors.yellow }}>Желтый</span> — склад продавца
                  </p>
                  <p>
                    Бывают товары где на каждый размер разная цена, то в таком случае будет отслеживаться цена и остальные ваши условия по цене на каждый отслеживаемый размер
                  </p>
                </div>
                <div style={{ ...infoModalStyles.listItem, flexDirection: 'column', gap: '16px' }}>
                  <strong>3. Желаемая цена</strong>
                  <p>Если цена достигнет указанного значения (+20₽), придёт уведомление.</p>
                  <p>⚠️После достижения цели товар ставится на паузу!</p>
                </div>
                <div style={{ ...infoModalStyles.listItem, flexDirection: 'column', gap: '16px' }}>
                  <strong>4. Уведомления</strong>
                  <p>
                    Первый чекбокс определяет, уведомлять ли при снижении цены.
                  </p>
                </div>
                <div style={{ ...infoModalStyles.listItem, flexDirection: 'column', gap: '16px' }}>
                  <strong>5. Диапазон цен</strong>
                  <p>Отображает реальный диапазон цен на основе исторических данных.</p>
                </div>
                <div style={{ ...infoModalStyles.listItem, flexDirection: 'column', gap: '16px' }}>
                  <strong>6. Дата последнего обновления</strong>
                  <p>Вверху справа отображается дата последнего обновления, в формете день.месяц</p>
                  <p>Всю историю изменения цен можно посмотреть в диапазоне</p>
                </div>
              </div>
              <div
                style={{
                  marginTop: '24px',
                  padding: '16px',
                  borderRadius: '8px',
                  background: theme === 'dark' ? 'rgba(76, 175, 80, 0.1)' : '#e8f5e9',
                  border: `1px solid ${themeColors.green}33`,
                }}
              >
                <p>💡<strong>Смысл:</strong> Это позволяет легко отслеживать нужные товары на Wildberries и не пропустить выгодную цену.</p>
                <p>
                  Например, если ждёте скидки на определенные кроссовки, добавьте артикул, выберите нужный размер и укажите желаемую цену — приложение пришлёт уведомление, как только цена упадёт.
                </p>
                <p>Можно настроить чтоб уведомления приходили на снижение цены, на технику какую-то , электронику итд</p>
              </div>
            </div>
          )}

          {!isHomePage && !isWaitingListPage && (
            <div style={infoModalStyles.section}>
              <p>Описание недоступно для этой страницы.</p>
            </div>
          )}
        </Modal.Body>
      </Modal >
    </>
  );
};

export default BottomNav;

